import React, { useMemo, useState } from 'react';
import { Link, router } from '@inertiajs/react';
import Layout from '../../components/Layout.jsx';

export default function AssignTrainingIndex({ facility, available_licenses = [], unassigned_members = [] }) {
  const [licenseId, setLicenseId] = useState('');
  const [selectedIds, setSelectedIds] = useState(() => new Set());
  const [submitting, setSubmitting] = useState(false);

  const selectedLicense = useMemo(
    () => available_licenses.find((fl) => String(fl.license_id) === licenseId),
    [available_licenses, licenseId],
  );

  const seatsAvailable = selectedLicense?.quantity_available ?? 0;
  const selectedCount = selectedIds.size;
  const overSelected = selectedCount > seatsAvailable;
  const canSubmit = !!selectedLicense && selectedCount > 0 && !overSelected && !submitting;

  const toggleMember = (id) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const toggleAll = () => {
    if (selectedIds.size === unassigned_members.length) {
      setSelectedIds(new Set());
    } else {
      setSelectedIds(new Set(unassigned_members.map((m) => m.id)));
    }
  };

  const submit = () => {
    if (!canSubmit) return;
    setSubmitting(true);
    router.post(
      `/facilities/${facility.id}/billing/bulk_assign_license`,
      { license_id: selectedLicense.license_id, user_ids: Array.from(selectedIds) },
      {
        onFinish: () => setSubmitting(false),
        onSuccess: () => setSelectedIds(new Set()),
      },
    );
  };

  const hasLicenses = available_licenses.length > 0;
  const hasMembers = unassigned_members.length > 0;

  return (
    <Layout>
      <div className="max-w-6xl mx-auto p-6 sm:p-8 space-y-6">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-gray-900">Assign Training</h1>
          <p className="mt-1 text-sm text-gray-500">Pick a training, then choose which team members get seats.</p>
        </div>

        {!hasLicenses ? (
          <EmptyState
            title="No training seats available"
            body="Purchase training seats before you can assign them."
            cta={{ href: `/facilities/${facility.id}/billing/purchase`, label: 'Purchase Training' }}
          />
        ) : !hasMembers ? (
          <EmptyState
            title="Everyone has training assigned"
            body="All team members already have an active training license."
            cta={{ href: '/team', label: 'Go to Team Members' }}
          />
        ) : (
          <>
            <SectionCard
              step={1}
              title="Choose training"
              description="Select which training course to assign from your available seats."
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
                </svg>
              }
            >
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {available_licenses.map((fl) => {
                  const checked = licenseId === String(fl.license_id);
                  return (
                    <label
                      key={fl.id}
                      className={`flex items-start gap-3 cursor-pointer p-4 border-2 rounded-xl transition-colors ${
                        checked ? 'border-indigo-600 bg-indigo-50' : 'border-gray-200 hover:border-indigo-300'
                      }`}
                    >
                      <input
                        type="radio"
                        name="license"
                        value={fl.license_id}
                        checked={checked}
                        onChange={() => setLicenseId(String(fl.license_id))}
                        className="mt-1 h-4 w-4 text-indigo-600"
                      />
                      <div className="min-w-0 flex-1">
                        <p className="text-sm font-bold text-gray-900">{fl.license_name}</p>
                        <p className="text-xs text-gray-500 mt-0.5">
                          <span className="font-semibold text-gray-700">{fl.quantity_available}</span> of {fl.quantity_purchased} seats available
                        </p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </SectionCard>

            <SectionCard
              step={2}
              title="Select team members"
              description="Choose who should receive a seat. Only members without an active license are listed."
              icon={
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M17 20h5v-2a4 4 0 00-3-3.87M9 20H4v-2a4 4 0 013-3.87m6-5.13a4 4 0 11-8 0 4 4 0 018 0zm6 0a4 4 0 11-8 0 4 4 0 018 0z" />
                </svg>
              }
              meta={
                <span className="inline-flex items-center rounded-full bg-gray-100 px-2.5 py-1 text-xs font-medium text-gray-600">
                  {unassigned_members.length} unassigned
                </span>
              }
            >
              <div className="flex items-center justify-between border-b border-gray-100 pb-3 mb-2">
                <label className="inline-flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={selectedIds.size === unassigned_members.length && unassigned_members.length > 0}
                    onChange={toggleAll}
                    className="h-4 w-4 rounded text-indigo-600"
                  />
                  <span className="text-sm font-medium text-gray-700">Select all</span>
                </label>
                <span className={`text-sm font-medium ${overSelected ? 'text-rose-600' : 'text-gray-600'}`}>
                  {selectedCount} selected
                  {selectedLicense && ` / ${seatsAvailable} available`}
                </span>
              </div>

              <div className="divide-y divide-gray-100">
                {unassigned_members.map((m) => {
                  const checked = selectedIds.has(m.id);
                  return (
                    <label
                      key={m.id}
                      className="flex items-center gap-3 py-3 cursor-pointer hover:bg-gray-50 rounded-lg px-2 -mx-2 transition"
                    >
                      <input
                        type="checkbox"
                        checked={checked}
                        onChange={() => toggleMember(m.id)}
                        className="h-4 w-4 rounded text-indigo-600"
                      />
                      <div className="shrink-0 w-9 h-9 bg-gradient-to-br from-indigo-500 to-purple-600 text-white rounded-full flex items-center justify-center text-xs font-semibold">
                        {m.display_name?.split(' ').map((p) => p[0]).slice(0, 2).join('').toUpperCase() || '?'}
                      </div>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <p className="font-semibold text-gray-900 text-sm">{m.display_name}</p>
                          {m.status === 'invited' && (
                            <span className="inline-flex px-2 py-0.5 rounded-full text-xs font-medium bg-amber-50 text-amber-700 border border-amber-200">
                              Invited
                            </span>
                          )}
                        </div>
                        <p className="text-xs text-gray-500">{m.email} <span className="capitalize">· {m.role}</span></p>
                      </div>
                    </label>
                  );
                })}
              </div>
            </SectionCard>

            <div className="sticky bottom-4 bg-white border border-gray-200 rounded-2xl shadow-lg p-4 flex flex-col sm:flex-row sm:items-center gap-3">
              <div className="flex-1 text-sm">
                {overSelected ? (
                  <p className="text-rose-600 font-medium">
                    Only {seatsAvailable} seats available for this training. Deselect {selectedCount - seatsAvailable} to continue.
                  </p>
                ) : selectedCount > 0 && selectedLicense ? (
                  <p className="text-gray-700">
                    Assigning <span className="font-semibold">{selectedCount}</span> {selectedLicense.license_name} {selectedCount === 1 ? 'seat' : 'seats'}.
                  </p>
                ) : (
                  <p className="text-gray-500">Choose a training and select team members to assign.</p>
                )}
              </div>
              <button
                onClick={submit}
                disabled={!canSubmit}
                className="inline-flex items-center justify-center gap-2 rounded-lg bg-indigo-600 px-5 py-2.5 text-sm font-semibold text-white hover:bg-indigo-700 disabled:opacity-50 disabled:cursor-not-allowed shadow-sm transition cursor-pointer"
              >
                {submitting ? (
                  <>
                    <svg className="w-4 h-4 animate-spin" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4z"></path>
                    </svg>
                    Assigning…
                  </>
                ) : `Assign ${selectedCount || ''} ${selectedCount === 1 ? 'Seat' : 'Seats'}`.trim()}
              </button>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

function SectionCard({ step, icon, title, description, meta, children }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-6 shadow-sm">
      <div className="flex items-start gap-3 mb-5">
        <div className="relative w-8 h-8 bg-indigo-50 text-indigo-600 rounded-lg flex items-center justify-center shrink-0">
          {icon}
          {step != null && (
            <span className="absolute -top-1.5 -right-1.5 w-5 h-5 bg-indigo-600 text-white rounded-full flex items-center justify-center text-[10px] font-bold ring-2 ring-white">
              {step}
            </span>
          )}
        </div>
        <div className="min-w-0 flex-1">
          <h3 className="text-lg font-semibold text-gray-900">{title}</h3>
          {description && <p className="text-sm text-gray-500 mt-0.5">{description}</p>}
        </div>
        {meta && <div className="shrink-0">{meta}</div>}
      </div>
      {children}
    </div>
  );
}

function EmptyState({ title, body, cta }) {
  return (
    <div className="bg-white border border-gray-200 rounded-2xl p-10 shadow-sm">
      <div className="max-w-md mx-auto text-center">
        <div className="mx-auto w-16 h-16 bg-gradient-to-br from-indigo-50 to-purple-50 text-indigo-600 rounded-2xl flex items-center justify-center mb-5">
          <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 6.253v13m0-13C10.832 5.477 9.246 5 7.5 5S4.168 5.477 3 6.253v13C4.168 18.477 5.754 18 7.5 18s3.332.477 4.5 1.253m0-13C13.168 5.477 14.754 5 16.5 5c1.747 0 3.332.477 4.5 1.253v13C19.832 18.477 18.247 18 16.5 18c-1.746 0-3.332.477-4.5 1.253" />
          </svg>
        </div>
        <h2 className="text-xl font-semibold text-gray-900 mb-2">{title}</h2>
        <p className="text-gray-600 mb-6">{body}</p>
        <Link
          href={cta.href}
          className="inline-flex items-center gap-2 px-5 py-2.5 text-sm font-semibold rounded-lg text-white bg-indigo-600 hover:bg-indigo-700 shadow-sm transition"
        >
          {cta.label}
        </Link>
      </div>
    </div>
  );
}
